import { SelectBoxOpt } from '@/components/content/SelectBox'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { resolveScheduleShipper } from '@/pages/outbound/scheduleShipper'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { useGetAllowedShipperCodes } from '@/services/shipper/useShipperQueries'
import { PLATFORM_OPTIONS } from '../constants'
import { OutboundSchedulePageQueryParams, OutboundScheduleShipperCode } from '../types/query'

type ScheduleShipper = {
  options: SelectBoxOpt<OutboundScheduleShipperCode>[]
  /** 허용 화주로 확정된 화주. 허용 화주 조회 중이거나 허용 선택지가 0개면 undefined */
  shipperCode: OutboundScheduleShipperCode | undefined
  isLoading: boolean
}

/**
 * 출고일정관리 2.5 화주 선택지·선택값 (LOGISPRD-12495)
 * - 허용 화주를 받기 전에는 화주를 정하지 않는다 — 기본값으로 먼저 조회하면 허용 밖 화주 요청이 나간다
 * - 헤더(index.tsx)와 그리드(useCutOffDate)가 이 한 곳의 결정을 같이 쓴다
 */
export const useScheduleShipper = (): ScheduleShipper => {
  const { parsedQs } = useParsedQuery<OutboundSchedulePageQueryParams>()
  const { data: allowedShipperCodes, isPending } = useGetAllowedShipperCodes()

  // feature.usedBundle on이면 플랫폼 선택지에서 MUSINSA_USED 제외 — 기존 query param 이 MUSINSA_USED 여도 첫 값으로 보정된다
  const { data: usedBundleEnabled, isPending: isUsedBundlePending } = useFeatureFlag('feature.usedBundle')
  const platformOptions = usedBundleEnabled ? PLATFORM_OPTIONS.filter(({ value }) => value !== 'MUSINSA_USED') : PLATFORM_OPTIONS

  // flag 도 기다린다 — 먼저 열면 곧 걸러질 MUSINSA_USED 로 조회가 한 번 나간다. flag 조회는 실패해도 false 로 끝나 영구 대기하지 않는다
  if (isPending || isUsedBundlePending) {
    return { options: [], shipperCode: undefined, isLoading: true }
  }

  return {
    ...resolveScheduleShipper({
      options: platformOptions,
      requested: parsedQs.shipperCode,
      // 첫 조회 실패면 undefined 라 고정 선택지로 내려가고 판정은 서버 가드에 맡긴다.
      // isError 로 가르지 않는다 — 재조회가 실패해도 react-query 는 직전 성공 값을 data 에 남기므로,
      // 버리면 허용 화주가 좁은 사용자의 선택이 고정 선택지 첫 값으로 조용히 넓어진다.
      allowedShipperCodes,
    }),
    isLoading: false,
  }
}
